<script setup lang="ts">
import { QuoteSignedEvent, formatEuros, isZeroAmount } from '@cockpit/shared';
import { computed, nextTick, onBeforeUnmount, onMounted, ref, watch } from 'vue';
import { useRouter } from 'vue-router';
import { playFanfare, prefersReducedMotion, runConfetti } from '../celebration';
import { useAuthStore } from '../stores/auth';
import { useCelebrationStore } from '../stores/celebration';
import { useStreamStore } from '../stores/stream';

/**
 * Fête de signature (maquette `#party`, `celebrate()`), montée une fois dans la coquille : à chaque
 * événement SSE `quote.signed`, la carte « Devis signé », les confettis (sauf mouvement réduit) et
 * la fanfare (si la préférence « son » est active).
 *
 * Lot Q : un devis à 0 € (signature d'un abonnement) est fêté aussi, mais sans « Préparer le projet » (il n'y a
 * pas de projet à planifier) : un seul bouton « Super ! » qui ferme la fête.
 */
const store = useCelebrationStore();
const auth = useAuthStore();
const stream = useStreamStore();
const router = useRouter();
const canvas = ref<HTMLCanvasElement | null>(null);
const goButton = ref<HTMLButtonElement | null>(null);
const zero = computed(() => (store.current ? isZeroAmount(store.current.amountCents) : false));

let unsubscribe: (() => void) | null = null;

watch(
  () => store.current,
  async (value, previous) => {
    if (!value || value === previous) return;
    if (auth.me?.preferences.sound ?? true) playFanfare();
    await nextTick();
    if (canvas.value && !prefersReducedMotion()) runConfetti(canvas.value, () => store.current === value);
    goButton.value?.focus();
  },
);

async function prepare(): Promise<void> {
  const quoteId = store.current?.quoteId ?? null;
  store.close();
  await router.push(quoteId ? { path: '/projets', query: { prepare: quoteId } } : { path: '/projets' });
}

function onKeydown(event: KeyboardEvent): void {
  if (event.key === 'Escape' && store.current) store.close();
}

onMounted(() => {
  unsubscribe = stream.on('quote.signed', QuoteSignedEvent, (event) => store.celebrate({ ...event }));
  document.addEventListener('keydown', onKeydown);
});
onBeforeUnmount(() => {
  unsubscribe?.();
  document.removeEventListener('keydown', onKeydown);
});
</script>

<template>
  <div
    v-if="store.current"
    id="party"
    class="party"
    role="dialog"
    aria-modal="true"
    aria-labelledby="partyName"
    data-testid="signature-party"
  >
    <canvas ref="canvas" aria-hidden="true"></canvas>
    <div class="party-card">
      <span class="eyebrow">Devis signé</span>
      <h2 id="partyName">{{ store.current.clientName }}</h2>
      <span class="num">{{ formatEuros(store.current.amountCents) }} HT</span>
      <span class="muted" style="font-size: 13px">{{ store.current.description }}</span>
      <span v-if="zero" class="muted" style="font-size: 12px" data-testid="party-zero"
        >Devis à 0 € : abonnement signé, pas de projet à préparer.</span
      >
      <div class="row">
        <template v-if="zero">
          <button ref="goButton" class="btn primary" type="button" @click="store.close()">Super !</button>
        </template>
        <template v-else>
          <button ref="goButton" class="btn primary" type="button" @click="prepare">
            Préparer le projet
          </button>
          <button class="btn" type="button" @click="store.close()">Plus tard</button>
        </template>
      </div>
    </div>
  </div>
</template>
